import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail, Phone, MapPin, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import ContactForm from '../components/ContactForm';

const SolicitudesYContacto = () => {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-zinc-950 via-zinc-900 to-black">
      {/* Header */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? 'bg-zinc-900/95 backdrop-blur-md shadow-xl' : 'bg-zinc-900/50 backdrop-blur-sm'
      }`}>
        <nav className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_166ab1a5-8f01-460c-943b-4303f928c33d/artifacts/wk6h830f_LogoSinFondo.png" 
                alt="Integralia Logo" 
                className="h-14 w-14 transition-transform duration-300 hover:scale-110"
                style={{ filter: 'drop-shadow(0 0 8px rgba(255, 255, 255, 0.3))' }}
              />
              <div>
                <h1 className="text-lg font-bold text-amber-400">INTEGRALIA</h1>
                <p className="text-xs text-amber-200">Precooperativa Multiactiva</p>
              </div>
            </div>
            
            <Button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 bg-amber-600/20 hover:bg-amber-600/30 text-amber-400 border border-amber-600/50"
            >
              <ArrowLeft className="w-4 h-4" />
              Volver al Inicio
            </Button>
          </div>
        </nav>
      </header>

      {/* Main Content */}
      <main className="pt-32 pb-20">
        <div className="container mx-auto px-6">
          {/* Hero Section */}
          <div className="text-center mb-16 animate-fade-in">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
              Solicitudes y <span className="text-amber-400">Contacto</span>
            </h1>
            <p className="text-base sm:text-lg text-amber-100/80 max-w-3xl mx-auto">
              Estamos aquí para ayudarte. Envíanos tu consulta y nos pondremos en contacto contigo lo antes posible.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-12 max-w-7xl mx-auto">
            {/* Contact Information */}
            <div className="lg:col-span-1 space-y-8">
              <div className="bg-gradient-to-br from-zinc-900/80 to-zinc-950/80 backdrop-blur-lg rounded-2xl p-8 border border-amber-600/20 shadow-2xl">
                <h2 className="text-2xl font-bold text-amber-400 mb-6">Información de Contacto</h2>
                
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="bg-amber-600/20 p-3 rounded-lg">
                      <Mail className="w-6 h-6 text-amber-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-white mb-1">Email</h3>
                      <a 
                        href="mailto:administracion@esalintegralia.com" 
                        className="text-amber-200 hover:text-amber-400 transition-colors text-sm"
                      >
                        administracion@esalintegralia.com
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="bg-amber-600/20 p-3 rounded-lg">
                      <Phone className="w-6 h-6 text-amber-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-white mb-1">Teléfono</h3>
                      <p className="text-amber-200 text-sm">+57 (300) 123 4567</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="bg-amber-600/20 p-3 rounded-lg">
                      <MapPin className="w-6 h-6 text-amber-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-white mb-1">Ubicación</h3>
                      <p className="text-amber-200 text-sm">
                        Antioquia, Chocó, Caldas<br/>
                        Colombia
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="bg-amber-600/20 p-3 rounded-lg">
                      <Clock className="w-6 h-6 text-amber-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-white mb-1">Horario</h3>
                      <p className="text-amber-200 text-sm">
                        Lunes a Viernes<br/>
                        8:00 AM - 5:00 PM
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Additional Info Card */}
              <div className="bg-gradient-to-br from-amber-900/20 to-amber-950/20 backdrop-blur-lg rounded-2xl p-8 border border-amber-600/30 shadow-2xl">
                <h3 className="text-lg font-bold text-amber-400 mb-4">¿Qué puedes consultar?</h3>
                <ul className="space-y-3 text-sm text-amber-100/80">
                  <li className="flex items-start gap-2">
                    <span className="text-amber-400 mt-1">•</span>
                    <span>Comercialización de minerales</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-400 mt-1">•</span>
                    <span>Cumplimiento LAFT</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-400 mt-1">•</span>
                    <span>Afiliación a la cooperativa</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-400 mt-1">•</span>
                    <span>Redes de minería ancestral</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-400 mt-1">•</span>
                    <span>Información general</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className="bg-gradient-to-br from-zinc-900/80 to-zinc-950/80 backdrop-blur-lg rounded-2xl p-8 sm:p-12 border border-amber-600/20 shadow-2xl">
                <h2 className="text-3xl font-bold text-white mb-2">Envíanos un mensaje</h2>
                <p className="text-amber-100/70 mb-8">
                  Completa el formulario y nos pondremos en contacto contigo en breve.
                </p>
                <ContactForm />
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-black/50 border-t border-amber-600/20 py-8">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_166ab1a5-8f01-460c-943b-4303f928c33d/artifacts/wk6h830f_LogoSinFondo.png" 
                alt="Integralia Logo" 
                className="h-10 w-10"
              />
              <div>
                <h3 className="text-sm font-bold text-amber-400">INTEGRALIA</h3>
                <p className="text-xs text-amber-200">Precooperativa Multiactiva</p>
              </div>
            </div>
            <p className="text-xs text-amber-100/60 text-center">
              © 2024 Integralia. Todos los derechos reservados.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SolicitudesYContacto;
